// Financial Freedom Calculator -- the feature orchestrator.
//
// Composes the two-pane experience: a scrollable inputs panel on the left and a
// sticky answers dashboard on the right. All network state comes from the
// single TanStack query in useFinancialFreedomCalculator; every card reads from
// that one unified payload, guaranteeing mathematical consistency across the UI.
//
// Currency lives here (frontend-only, presentational) and flows down to both
// panels so a global visitor can express every figure in their own currency.

import React, { useState } from 'react';

import { ForgeUltraHook } from './components/ForgeUltraHook';
import { InputsPanel } from './components/InputsPanel';
import { ResultsPanel } from './components/ResultsPanel';
import { TrajectoryChart } from './components/TrajectoryChart';
import { useFinancialFreedomCalculator } from './hooks/useFinancialFreedomCalculator';
import { DEFAULT_CURRENCY } from './utils/currency';

export const FinancialFreedomCalculator: React.FC = () => {
    const { inputs, updateInput, data, isLoading, isFetching, isError, error } =
        useFinancialFreedomCalculator();
    const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8 font-sans">
            <div className="mb-8 text-center sm:text-left">
                <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                    Financial Freedom Calculator
                </h1>
                <p className="text-slate-500 dark:text-slate-300 mt-3 text-lg max-w-3xl">
                    Find the answers to your questions: When can you safely retire? What amount of savings do you need
                    to retire today? What average return and which asset allocation in your investment portfolio do you
                    need to retire today?
                </p>
            </div>

            {isError && (
                <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-600 text-red-800 rounded shadow-sm font-medium">
                    <p className="font-bold mb-1">Calculation Error</p>
                    <p className="text-sm">
                        {error?.message ||
                            'An unexpected mathematical bounds error occurred.'}
                    </p>
                </div>
            )}

            {/* Two-pane grid */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Left column (inputs) -- stretches to the full height of the
                    right column (grid items default to `stretch`) so the card
                    reaches all the way down to the Forge Ultra hook. This means
                    an opened "+ Add" section reveals its inputs inline with no
                    uncomfortable internal scrolling. */}
                <div className="lg:col-span-4 pr-1">
                    <InputsPanel
                        inputs={inputs}
                        updateInput={updateInput}
                        currency={currency}
                        onCurrencyChange={setCurrency}
                    />
                </div>

                {/* Right column (results) */}
                <div className="lg:col-span-8 flex flex-col gap-6 lg:sticky lg:top-8 self-start">
                    {isLoading && !data ? (
                        <div className="flex-1 flex items-center justify-center bg-white rounded-2xl shadow-sm border border-slate-200 min-h-[500px]">
                            <div className="flex flex-col items-center">
                                <div className="w-10 h-10 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                                <span className="text-slate-500 font-medium">
                                    Initializing institutional engines...
                                </span>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Answer cards */}
                            <ResultsPanel
                                data={data}
                                inputs={inputs}
                                isFetching={isFetching}
                                currency={currency}
                            />

                            {/* Wealth trajectory chart */}
                            {data && (
                                <div className="flex-1 bg-white rounded-2xl shadow-sm border border-slate-200 p-6 flex flex-col min-h-[450px]">
                                    <div className="flex justify-between items-end mb-6">
                                        <div>
                                            <h3 className="text-lg font-extrabold text-slate-900">
                                                Your Wealth Trajectory Projection
                                            </h3>
                                            <p className="text-xs text-slate-500 font-medium mt-1">
                                                Modeled in Inflation-Adjusted Present-Day Value
                                            </p>
                                        </div>
                                        {isFetching && (
                                            <span className="text-xs font-bold text-emerald-600 animate-pulse bg-emerald-50 px-2 py-1 rounded">
                                                Recalculating...
                                            </span>
                                        )}
                                    </div>

                                    {/* Recharts container. Needs an EXPLICIT
                                        height: Recharts' ResponsiveContainer
                                        measures its parent, and a percentage
                                        height chain through a flex-1 parent whose
                                        column height is `auto` collapses to 0
                                        (the chart renders invisibly). A fixed
                                        height guarantees it draws. The -ml-4
                                        offsets the Y-axis margin so the graph's
                                        left edge aligns with the heading above. */}
                                    <div className="h-[420px] -ml-4">
                                        <TrajectoryChart
                                            data={data.trajectory}
                                            isFetching={isFetching}
                                            currency={currency}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* The Forge Ultra conversion hook */}
                            {data && <ForgeUltraHook hookText={data.forge_ultra_hook} />}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
